import Close from '@mui/icons-material/Close'
import Tv from '@mui/icons-material/Tv'
import {
  Box,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { getSupportKiosks } from 'src/supportPortal/services/oohManagementApi'

function statusColor(status) {
  const s = (status?.status || status || '').toString().toLowerCase()
  if (s.includes('active') || s.includes('online') || s.includes('live')) return 'success'
  if (s.includes('offline') || s.includes('inactive')) return 'default'
  return 'warning'
}

export default function KioskScreensDrawer({ open, clinicLocation, onClose }) {
  const clinicLocationId = clinicLocation?.id
  const [loading, setLoading] = useState(false)
  const [kiosks, setKiosks] = useState([])
  const [brandName, setBrandName] = useState('')

  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!open || !clinicLocationId) return
      setLoading(true)
      try {
        const resp = await getSupportKiosks(clinicLocationId)
        if (cancelled) return
        if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
          const data = getDataMapFromCommonResponse(resp)
          setKiosks(data?.kiosks || [])
          setBrandName(data?.brand?.name || '')
        } else {
          setKiosks([])
        }
      } catch (e) {
        console.error('load kiosks failed', e)
        if (!cancelled) setKiosks([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [open, clinicLocationId])

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 560, maxWidth: '95vw', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            p: 3,
            background: 'linear-gradient(135deg, #1a2332 0%, #243044 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              AUMHUM Kiosks
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {clinicLocation?.title || clinicLocation?.name}
              {brandName ? ` · ${brandName}` : ''}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#fff' }}>
            <Close />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
            AUMHUM screen devices registered for this clinic brand.
          </Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : kiosks.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'text.secondary', p: 2 }}>
              No AUMHUM kiosks registered for this brand yet.
            </Typography>
          ) : (
            kiosks.map((k) => (
              <Box
                key={k.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  mb: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                }}
              >
                <Tv sx={{ color: 'text.secondary' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600 }} noWrap>
                    {k.title || k.id}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                    {k.installationType || 'screen'} · {k.id}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  color={statusColor(k.kioskStatus)}
                  label={(k.kioskStatus?.status || 'unknown').toString()}
                />
              </Box>
            ))
          )}
        </Box>
      </Box>
    </Drawer>
  )
}
